import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet";
import axios from "axios";

// Detect platform synchronously before render
const isIOS = /iphone|ipad|ipod/.test(navigator.userAgent.toLowerCase());
const isAndroid = /android/.test(navigator.userAgent.toLowerCase());
const isIOSSafari =
  isIOS &&
  /safari/.test(navigator.userAgent.toLowerCase()) &&
  !/crios|fxios|edgios|opios/.test(navigator.userAgent.toLowerCase());
const isIOSNonSafari = isIOS && !isIOSSafari;

const PathwayRedirect = () => {
  const { id } = useParams();
  const [pathway, setPathway] = useState(null);
  const [loading, setLoading] = useState(true);
  const [description, setDescription] = useState(
    "Explore this pathway on WithYou"
  );

  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  useEffect(() => {
    const fetchPathway = async () => {
      try {
        // Fetch pathway data from cloud function API
        const response = await axios.get(
          `https://app-k3hvgfckdq-uc.a.run.app/api/pathways/${id}`
        );
        setPathway(response.data);

        // Get first step content for description if available
        if (
          response.data.steps &&
          Object.values(response.data.steps).length > 0
        ) {
          const firstStep = Object.values(response.data.steps)[0];
          if (firstStep.data) {
            setDescription(
              firstStep.data.substring(0, 200) +
                (firstStep.data.length > 200 ? "..." : "")
            );
          }
        }
      } catch (error) {
        console.error("Error fetching pathway:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchPathway();

    // For Android, use Play Store URL with referrer
    if (isAndroid) {
      const encodedPathway = encodeURIComponent(id);
      window.location.href = `https://play.google.com/store/apps/details?id=com.metastudiosinc.withyou&referrer=pathway%3D${encodedPathway}`;
    }
  }, [id, isLocalhost]);

  // Image URL with fallback
  const imageUrl =
    pathway?.background?.src ||
    pathway?.image ||
    "https://pathways.withyou.net/static/media/pathway.9f2c6fd6.jpeg";

  const universalLink = `https://pathways.withyou.net/pathway-share/${id}`;
  const safariUniversalLink = `x-safari-https://pathways.withyou.net/pathway-share/${id}`;
  const webUrl = `https://pathways.withyou.net/pathways/og/${id}`;
  const shouldShowOpenInWithYou = !isIOS || isIOSSafari;

  return (
    <>
      {/* Static OGP tags served directly for crawlers */}
      <Helmet>
        <title>{pathway?.name || "WithYou Pathway"}</title>

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="article" />
        <meta property="og:url" content={webUrl} />
        <meta
          property="og:title"
          content={pathway?.name || "WithYou Pathway"}
        />
        <meta property="og:description" content={description} />
        <meta property="og:image" content={imageUrl} />
        <meta property="og:site_name" content="WithYou" />

        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={webUrl} />
        <meta
          property="twitter:title"
          content={pathway?.name || "WithYou Pathway"}
        />
        <meta property="twitter:description" content={description} />
        <meta property="twitter:image" content={imageUrl} />

        {/* App links */}
        <meta property="al:ios:app_name" content="WithYou" />
        <meta property="al:ios:url" content={universalLink} />
        <meta property="al:android:url" content={universalLink} />
        <meta property="al:android:app_name" content="WithYou" />
        {/* iOS Smart App Banner - using JSX interpolation */}
        <meta
          name="apple-itunes-app"
          content={`app-id=1616729093, app-argument=${universalLink}`}
        />
      </Helmet>

      {/* User-facing content */}
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          padding: "20px",
        }}
      >
        <div style={{ maxWidth: "600px", textAlign: "center" }}>
          <h1>{pathway?.name || "WithYou Pathway"}</h1>

          {loading ? (
            <p>Loading pathway...</p>
          ) : (
            <>
              <p>{description}</p>
              <div style={{ margin: "30px 0" }}>
                <img
                  src={imageUrl}
                  alt={pathway?.name || "WithYou Pathway"}
                  style={{
                    maxWidth: "100%",
                    borderRadius: "8px",
                    marginBottom: "20px",
                  }}
                />

                <div>
                  {isIOSNonSafari && !isLocalhost && (
                    <a
                      href={safariUniversalLink}
                      style={{
                        display: "inline-block",
                        margin: "10px",
                        padding: "12px 24px",
                        background: "#1a73e8",
                        color: "white",
                        textDecoration: "none",
                        borderRadius: "4px",
                        fontWeight: "bold",
                      }}
                    >
                      Open in Safari
                    </a>
                  )}
                  {shouldShowOpenInWithYou && (
                    <a
                      href={universalLink}
                      style={{
                        display: "inline-block",
                        margin: "10px",
                        padding: "12px 24px",
                        background: "#1a73e8",
                        color: "white",
                        textDecoration: "none",
                        borderRadius: "4px",
                        fontWeight: "bold",
                      }}
                    >
                      Open in WithYou
                    </a>
                  )}

                  <a
                    href={`https://apps.apple.com/us/app/withyou/id1616729093?app-argument=${encodeURIComponent(
                      universalLink
                    )}`}
                    style={{
                      display: "inline-block",
                      margin: "10px",
                      padding: "12px 24px",
                      background: "#34a853",
                      color: "white",
                      textDecoration: "none",
                      borderRadius: "4px",
                      fontWeight: "bold",
                    }}
                  >
                    Download WithYou
                  </a>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PathwayRedirect;
